import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE, DISABLED_FEATURES } from '../config/api';
import { 
  Award, 
  BookOpen, 
  Users, 
  TrendingUp, 
  ArrowRight,
  CheckCircle,
  Target,
  Brain,
  Sparkles,
  Bot,
  Shield,
  Zap,
  LineChart,
  GraduationCap,
  Building2,
  Briefcase,
  ChevronRight,
  FlaskConical,
  BarChart3,
  Globe,
  FileCheck,
  Gauge,
  Route,
  BadgeCheck,
  Mail,
  MapPin,
  Linkedin,
  Star,
  Play,
  Bell,
  Lock,
  Cpu,
  Network,
  ClipboardCheck,
  Trophy,
  BookMarked,
  Layers,
  Settings,
  AlertTriangle,
  Calendar,
  CreditCard,
  Wallet,
  Check
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
const API = `${API_BASE}/api`;

// Cerebro Logo URL - Teal/Gold Neural Brain Hexagon
const CEREBRO_LOGO = "https://customer-assets.emergentagent.com/job_cpd-compass/artifacts/km6w43n1_Teal%20Cerebro%20Transp.png";

// Feature Card Component
const FeatureCard = ({ icon: Icon, title, description, highlight = false }) => (
  <div className={`group relative p-6 rounded-2xl border transition-all duration-300 hover:scale-[1.02] ${
    highlight 
      ? 'bg-gradient-to-br from-amber-500/10 to-teal-500/10 border-amber-500/30 hover:border-amber-500/50' 
      : 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600'
  }`}>
    <div className={`inline-flex p-3 rounded-xl mb-4 ${
      highlight ? 'bg-amber-500/20' : 'bg-teal-500/20'
    }`}>
      <Icon className={`h-6 w-6 ${highlight ? 'text-amber-400' : 'text-teal-400'}`} />
    </div>
    <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
    <p className="text-sm text-slate-400 leading-relaxed">{description}</p>
  </div>
);

// Pricing Card Component
const PricingCard = ({ tier, price, period, features, highlighted = false, cta = "Get Started" }) => (
  <div className={`relative rounded-2xl p-8 transition-all duration-300 ${
    highlighted 
      ? 'bg-gradient-to-b from-amber-500/20 to-slate-800/80 border-2 border-amber-500/50 scale-105 shadow-xl shadow-amber-500/10' 
      : 'bg-slate-800/50 border border-slate-700/50 hover:border-slate-600'
  }`}>
    {highlighted && (
      <div className="absolute -top-4 left-1/2 -translate-x-1/2">
        <span className="px-4 py-1.5 bg-amber-500 text-slate-900 text-sm font-bold rounded-full">
          MOST POPULAR
        </span>
      </div>
    )}
    <div className="text-center mb-6">
      <h3 className="text-xl font-bold text-white mb-2">{tier}</h3>
      <div className="flex items-baseline justify-center gap-1">
        <span className="text-4xl font-bold text-white">{price}</span>
        {period && <span className="text-slate-400">/{period}</span>}
      </div>
    </div>
    <ul className="space-y-3 mb-8">
      {features.map((feature, i) => (
        <li key={i} className="flex items-start gap-3">
          <Check className={`h-5 w-5 mt-0.5 flex-shrink-0 ${highlighted ? 'text-amber-400' : 'text-teal-400'}`} />
          <span className="text-sm text-slate-300">{feature}</span>
        </li>
      ))}
    </ul>
    <Link
      to="/login"
      className={`block w-full py-3 px-6 rounded-xl font-semibold text-center transition-all ${
        highlighted 
          ? 'bg-amber-500 hover:bg-amber-600 text-slate-900' 
          : 'bg-slate-700 hover:bg-slate-600 text-white'
      }`}
    >
      Sign In
    </Link>
  </div>
);

export default function LandingPage() {
  const { isAuthenticated } = useAuth();
  const [stats, setStats] = useState({
    organizations: 15,
    professionals: 2500,
    courses: 50,
    completionRate: 94
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/public/stats`).catch(() => null);
        if (res?.data) setStats(res.data);
      } catch (error) {
        console.error('Stats not available');
      }
    };
    fetchStats();
  }, []);

  // Enterprise Features
  const enterpriseFeatures = [
    {
      icon: Brain,
      title: "AI Course Generation",
      description: "Create comprehensive training courses in minutes with our AI engine. Every course is curated and approved by human experts ensuring Human-In-The-Loop (HITL) quality protocols.",
      highlight: true
    },
    {
      icon: Globe,
      title: "LMS-as-a-Service",
      description: "Deploy your own branded learning management system instantly. White-label solution with your domain, branding, and complete control - no infrastructure required.",
      highlight: true
    },
    {
      icon: FlaskConical,
      title: "Learning Labs",
      description: "Interactive sandbox environments where learners practice real-world scenarios in safe, simulated settings."
    },
    {
      icon: Network,
      title: "Multi-Tenant Architecture",
      description: "Deploy separate branded instances for different business units, franchises, or client organisations."
    },
    {
      icon: ClipboardCheck,
      title: "NAVI Coach & Assessment Builder",
      description: "AI-powered coaching with custom quizzes, MCQs, case studies, and practical assessments. Auto-grading and detailed analytics included."
    },
    {
      icon: Route,
      title: "Learning Pathways",
      description: "Design structured career progression paths with prerequisite courses and competency milestones."
    },
    {
      icon: Trophy,
      title: "Gamification Engine",
      description: "Boost engagement with points, badges, leaderboards, and achievement systems that motivate learners."
    },
    {
      icon: Users,
      title: "Learning Strategist Access",
      description: "Dedicated Organisational Learning Strategist for WSP and ATR assistance, mapping learning pathways and courses aligned to your skills plan.",
      highlight: true
    },
    {
      icon: BarChart3,
      title: "WSP & ATR Reporting",
      description: "Automated Workplace Skills Plan submissions and Annual Training Reports for SETA compliance."
    },
    {
      icon: Shield,
      title: "SCORM & AICC Compliance",
      description: "Import and track third-party content with full SCORM 1.2/2004 and AICC standard support."
    },
    {
      icon: Settings,
      title: "Role Readiness Assessments",
      description: "Evaluate employee competencies against role requirements with gap analysis and development plans."
    },
    {
      icon: Award,
      title: "Certificate Generation",
      description: "Automatically generate branded PDF certificates with QR verification codes upon course completion."
    },
    {
      icon: Lock,
      title: "SSO Integration",
      description: "Seamless Azure AD, Google, and SAML single sign-on for enterprise identity management."
    },
    {
      icon: LineChart,
      title: "Advanced Analytics",
      description: "Real-time dashboards tracking learner progress, completion rates, and organisational skills development."
    },
    {
      icon: Award,
      title: "Candidacy Portal",
      description: "Professional designation readiness management - integrating knowledge acquisition, practical application, workplace experience, competency development, mentoring, portfolio management, and professional designation readiness.",
      highlight: true
    }
  ];

  // Individual Features (filtered by DISABLED_FEATURES)
  const allIndividualFeatures = [
    {
      icon: GraduationCap,
      title: "Navigate Academy",
      description: "Access ICA and ICITP professional qualifications with globally recognised certifications in compliance and IT governance.",
      highlight: true,
      featureKey: 'navigate_academy'
    },
    {
      icon: Bell,
      title: "Regulatory Alerts",
      description: "Real-time notifications from FSCA, FIC, SARB, and international regulators. Stay ahead of compliance changes.",
      highlight: true,
      featureKey: 'regulatory_alerts'
    },
    {
      icon: Award,
      title: "Candidacy",
      description: "Professional designation readiness management - integrating knowledge acquisition, practical application, workplace experience, competency development, mentoring, portfolio management, and professional designation readiness in one intelligent ecosystem.",
      highlight: true,
      featureKey: 'candidacy'
    },
    {
      icon: BookMarked,
      title: "Self-Paced Learning",
      description: "Learn at your own pace with lifetime access to course materials, videos, and downloadable resources."
    },
    {
      icon: Target,
      title: "CPD Tracking",
      description: "Automatically log and track your Continuing Professional Development points across all completed courses."
    },
    {
      icon: Award,
      title: "Digital Certificates",
      description: "Earn verifiable digital certificates and badges to showcase your achievements on LinkedIn."
    },
    {
      icon: Brain,
      title: "AI Learning Copilot",
      description: "Get personalised learning recommendations and instant answers to your compliance questions."
    },
    {
      icon: Layers,
      title: "Structured Pathways",
      description: "Follow curated learning paths from foundational to advanced levels in your chosen specialisation."
    },
    {
      icon: Globe,
      title: "Global Standards",
      description: "Content aligned to international frameworks including FATF, Basel, GDPR, and ISO standards."
    }
  ];

  // Filter out disabled features
  const individualFeatures = allIndividualFeatures.filter(
    f => !f.featureKey || !DISABLED_FEATURES[f.featureKey]
  );

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/90 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src={CEREBRO_LOGO} alt="Cerebro Core" className="h-10 w-auto" />
              <span className="text-white font-bold text-lg hidden sm:block">Cerebro Core</span>
            </Link>
            <div className="flex items-center gap-3">
              <Link
                to="/login"
                className="bg-teal-500 hover:bg-teal-600 text-slate-900 text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-teal-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-[600px] h-[400px] bg-amber-500/5 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="absolute inset-0 bg-teal-500/20 blur-[100px] rounded-full scale-150" />
              <img 
                src={CEREBRO_LOGO} 
                alt="Cerebro" 
                className="relative h-40 sm:h-52 w-auto object-contain"
              />
            </div>
          </div>

          {/* Title */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/50 border border-slate-700 mb-6">
              <Sparkles className="h-4 w-4 text-amber-400" />
              <span className="text-sm text-slate-300">A Workforce Capability Platform</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              We Help Organisations Make{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-amber-400">
                Compliance Training Relevant
              </span>
            </h1>
            <p className="text-xl text-slate-400 max-w-4xl mx-auto leading-relaxed mb-6">
              Compliance training is difficult to keep current, engaging and relevant as regulations, risks and business requirements continuously change.
            </p>
            <p className="text-base text-slate-400 max-w-3xl mx-auto leading-relaxed mb-4">
              Cerebro Core transforms static training into continuous, intelligent learning — helping organisations keep content current, personalise learning by role and competency, improve engagement, identify skills gaps and measure real understanding.
            </p>
            <p className="text-lg font-semibold text-teal-400 max-w-3xl mx-auto">
              Move beyond course completion. Build measurable workforce capability.
            </p>
          </div>

          {/* Enterprise Only Badge */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex p-1.5 bg-slate-800/50 rounded-xl border border-slate-700">
              <div
                className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm bg-teal-500 text-slate-900 shadow-lg"
                data-testid="tab-enterprise"
              >
                <Building2 className="h-4 w-4" />
                For Enterprises
              </div>
            </div>
          </div>

          {/* Value Proposition */}
          <div className="text-center mb-12">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                Scale Learning Across Your Organisation
              </h2>
              <p className="text-slate-400 mb-8">
                Deploy on-demand training, track compliance, and develop your workforce with 
                enterprise-grade tools designed for regulated industries.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="#features"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-teal-500 text-slate-900 font-semibold rounded-xl hover:bg-teal-400 transition-all shadow-lg shadow-teal-500/25"
                >
                  <Play className="h-5 w-5" />
                  See Features
                </a>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-slate-800 text-white font-semibold rounded-xl border border-slate-700 hover:bg-slate-700 transition-all"
                >
                  Sign In
                </Link>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {[
              { value: `${stats.organizations}+`, label: 'Organisations' },
              { value: `${stats.professionals}+`, label: 'Professionals' },
              { value: `${stats.courses}+`, label: 'Courses' },
              { value: `${stats.completionRate}%`, label: 'Completion Rate' }
            ].map((stat, idx) => (
              <div key={idx} className="text-center p-4 rounded-xl bg-slate-800/30 border border-slate-800">
                <p className="text-3xl font-bold text-teal-400">{stat.value}</p>
                <p className="text-sm text-slate-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header with Tabs */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/50 border border-slate-700 mb-6">
              <Zap className="h-4 w-4 text-teal-400" />
              <span className="text-sm text-slate-300">Powerful Features</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Everything You Need to{' '}
              <span className="text-teal-400">Transform Learning</span>
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Comprehensive tools for enterprise learning management, compliance tracking, and workforce development.
            </p>
          </div>

          {/* Features Grid - Enterprise Only */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {enterpriseFeatures.map((feature, idx) => (
              <FeatureCard key={idx} {...feature} />
            ))}
          </div>
        </div>
      </section>

      {/* Platform Showcase */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-500/10 border border-teal-500/30 mb-6">
                <Brain className="h-4 w-4 text-teal-400" />
                <span className="text-sm text-teal-400 font-medium">AI-Powered Platform</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Intelligence Built Into{' '}
                <span className="text-teal-400">Every Layer</span>
              </h2>
              <p className="text-lg text-slate-400 mb-8 leading-relaxed">
                From AI-generated courses to intelligent recommendations, Cerebro leverages 
                cutting-edge technology to deliver personalised, effective learning experiences.
              </p>
              
              <div className="space-y-4">
                {[
                  { icon: Bot, title: "AI Course Generation", desc: "Turn documents, URLs or simple prompts into engaging courses in minutes, with human-in-the-loop review ensuring content is accurate, relevant and ready to publish" },
                  { icon: Sparkles, title: "Smart Recommendations", desc: "Personalised learning paths based on goals" },
                  { icon: Bell, title: "Real-time Alerts", desc: "Stay updated with regulatory changes" },
                  { icon: BarChart3, title: "Predictive Analytics", desc: "Identify skill gaps before they impact performance" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4 p-4 rounded-xl bg-slate-800/30 border border-slate-800">
                    <div className="p-2 rounded-lg bg-teal-500/20">
                      <item.icon className="h-5 w-5 text-teal-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">{item.title}</h4>
                      <p className="text-sm text-slate-400">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-teal-500/20 to-amber-500/20 rounded-3xl blur-3xl" />
              <div className="relative bg-slate-900 rounded-2xl border border-slate-800 p-8 shadow-2xl">
                {/* Mock Dashboard */}
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-teal-500/20 flex items-center justify-center">
                        <GraduationCap className="h-5 w-5 text-teal-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">Learning Dashboard</p>
                        <p className="text-xs text-slate-500">Real-time progress tracking</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-teal-400">87%</p>
                      <p className="text-xs text-slate-500">Completion</p>
                    </div>
                  </div>
                  
                  <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full w-[87%] bg-gradient-to-r from-teal-500 to-teal-400 rounded-full" />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { label: "Courses", value: "12" },
                      { label: "CPD Points", value: "45" },
                      { label: "Badges", value: "8" }
                    ].map((stat, idx) => (
                      <div key={idx} className="text-center p-3 rounded-lg bg-slate-800/50">
                        <p className="text-xl font-bold text-white">{stat.value}</p>
                        <p className="text-xs text-slate-500">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                  
                  <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-400" />
                      <div>
                        <p className="text-sm font-medium text-amber-400">New Regulatory Alert</p>
                        <p className="text-xs text-slate-400">FSCA Notice: Updated AML Requirements</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-teal-500/20 to-amber-500/20 rounded-3xl blur-3xl" />
            <div className="relative bg-slate-900 rounded-3xl border border-slate-800 p-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Ready to Transform Your{' '}
                <span className="text-teal-400">Learning Experience</span>?
              </h2>
              <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
                Join thousands of professionals and organisations already using Cerebro Core 
                to drive compliance excellence and workforce development.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="#features"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-teal-500 text-slate-900 font-semibold rounded-xl hover:bg-teal-400 transition-all"
                >
                  <Play className="h-5 w-5" />
                  Explore Features
                </a>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-slate-800 text-white font-semibold rounded-xl border border-slate-700 hover:bg-slate-700 transition-all"
                >
                  Sign In
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-950 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* Brand Column */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src={CEREBRO_LOGO} alt="Cerebro Core" className="h-8 w-auto" />
                <span className="text-white font-bold">Cerebro Core</span>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed max-w-md">
                Cerebro Core is the learning intelligence platform of the Navigate Compliance Group (Pty) Ltd - 
                a South African governance & compliance technology firm. QCTO Accredited.
              </p>
              <div className="flex items-center gap-2 mt-4 text-sm text-slate-500">
                <MapPin className="h-4 w-4" />
                Johannesburg, South Africa
              </div>
            </div>
            
            {/* Links Column */}
            <div className="flex flex-col md:items-end">
              <div className="flex items-center gap-4 mb-4">
                <a 
                  href="https://www.linkedin.com/company/navigate-compliance-group/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition-colors"
                >
                  <Linkedin className="h-5 w-5 text-slate-400" />
                  <span className="text-sm text-slate-300">LinkedIn</span>
                </a>
              </div>
              
              {/* Policy Links */}
              <div className="flex flex-wrap gap-4 text-sm">
                <a 
                  href="/responsible-ai"
                  className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  Responsible AI
                </a>
                <a 
                  href="https://customer-assets-7cd3h4nn.emergentagent.net/job_cpd-compass/artifacts/s6j6bxyk_Privacy%20Policy.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  Privacy Policy
                </a>
                <a 
                  href="https://customer-assets-7cd3h4nn.emergentagent.net/job_cpd-compass/artifacts/dtzmhzj0_Terms%20of%20Use.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  Terms of Use
                </a>
                <a 
                  href="https://customer-assets-7cd3h4nn.emergentagent.net/job_cpd-compass/artifacts/c94i8o5l_HITL%20Governance%20Policy.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  HITL Governance
                </a>
              </div>
            </div>
          </div>
          
          <div className="pt-8 border-t border-slate-800 text-center">
            <p className="text-sm text-slate-500">
              © 2026 Navigate Compliance Group (Pty) Ltd. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
